import React from 'react';
import { Helmet } from 'react-helmet-async';

export interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article' | 'product' | 'profile';
  twitterHandle?: string;
  structuredData?: object;
  canonical?: string;
  noIndex?: boolean;
  noFollow?: boolean;
}

export function SEOHead({
  title = "Rulevia - AI Ad Creative Generator & Campaign Optimizer | Multi-Platform",
  description = "Create high-performing ad creatives with AI in minutes. Rulevia generates, optimizes, and validates ads for Meta, Google, TikTok & more. Get compliance checks, performance predictions, and first-time approval. Start free trial.",
  keywords = "AI ad generator, ad creative generator, campaign optimizer, multi-platform ads, Meta ads, Google ads, TikTok ads, ad compliance, performance prediction, ad validation, first-time approval, AI advertising",
  image = "https://rulevia.com/page_thumbnail.jpg",
  url,
  type = "website",
  twitterHandle = "@rulevia",
  structuredData,
  canonical,
  noIndex = false,
  noFollow = false
}: SEOHeadProps) {
  const siteUrl = "https://rulevia.com";
  const fullUrl = url ? `${siteUrl}${url}` : siteUrl;
  const canonicalUrl = canonical || fullUrl;

  // Ensure image is absolute URL
  const absoluteImage = image?.startsWith('http') ? image : `${siteUrl}${image}`;

  // Construct robots meta tag
  const robotsContent = [
    noIndex ? 'noindex' : 'index',
    noFollow ? 'nofollow' : 'follow'
  ].join(', ');

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="robots" content={robotsContent} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph Tags */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={absoluteImage} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:site_name" content="Rulevia" />
      <meta property="og:locale" content="en_US" />

      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={twitterHandle} />
      <meta name="twitter:creator" content={twitterHandle} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={absoluteImage} />

      {/* Additional Meta Tags */}
      <meta name="theme-color" content="#3B82F6" />
      <meta name="msapplication-TileColor" content="#3B82F6" />

      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
}